import { defineStore } from 'pinia';
import APIClient from '~/api/apiClient';
import { useUIStore } from '~/store/uiStore';
import { useDataStore } from './dataStore';
import { BuyNowProduct } from '~/types';
import useGTag from '~/composables/useGTag';
import { useRoute } from 'vue-router';

function slugToProductName(slug: string): string {
  const nameMap: {[key: string]: string} = {
    'fatimealign': 'faTimeAlign',
    'faguitaralign': 'faGuitarAlign',
    'fairmageddon': 'faIRmageddon'
    // Fügen Sie hier weitere Zuordnungen hinzu
  };
  return nameMap[slug] || slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

const apiClient = APIClient.getInstance();

const { 
  trackAddToCart, 
  trackRemoveFromCart, 
  trackBeginCheckout, 
} = useGTag();

const useCartStore = defineStore({
  id: 'cart',
  state: () => ({
    cartData: null,
    quantity: 0,
    checkoutWithoutLoginInitiated: false // moved to cartStore
  }),
  actions: {
    setCheckoutWithLoginInitiated(initiated: boolean = true) {
      console.log('setCheckoutWithLoginInitiated:', initiated);
      this.checkoutWithoutLoginInitiated = initiated;
    },
    resetIntermediates()
    {
      this.setCheckoutWithLoginInitiated(false);
    },
    async fetchCart() {
      try {
        const response = await apiClient.getCart();
        if (response && response.valid && response.result) {
          const fetchedCart = response.result;
          console.log("Fetched cart:", fetchedCart);

          this.cartData = {
            products: fetchedCart.products.map((product, index) => ({
              ...product,
            })),
            total_regular: fetchedCart.total_regular,
            total_discount: fetchedCart.total_discount,
          };

          this.quantity = fetchedCart.products.reduce((sum, product) => sum + product.quantity, 0);
        }
      } catch (error) {
        console.error("Error fetching cart:", error);
      }
    },
    async addToCart(productName: string, quantity: number = 1) {
          
      const uiStore = useUIStore();
      const dataStore = useDataStore();
      const route = useRoute();

      if (productName === undefined) {
        const slug = route.params.slug as string;
        const mappedName = slugToProductName(slug);
        
        // Erstelle einen Error mit Informationen über die Ersetzung
        const warning = new Error(`Product name was undefined. Used mapped name from slug: "${mappedName}"`);
        // Logge die Warnung in der Konsole
        console.warn('Product name mapping warning:', warning);

        // Verwende den gemappten Namen für die weitere Verarbeitung
        productName = mappedName;
      }

      if (productName === undefined) {
        uiStore.setPopupMessage(`Error. Could not add item to cart.`);


        throw new Error("Product name cannot be undefined when adding to cart");
       }
    
      // Check if the product already exists in the cart
      const existingProduct = this.cartData?.products.find(product => product.name === productName);
      if (existingProduct !== undefined && existingProduct.quantity >= 5) {
        uiStore.setPopupMessage(`Reached cart limit for this item.`);
        return;
      }

      if(dataStore.isFreeProduct(productName))
      {
        uiStore.setPopupMessage(`Error: Can't add a free product. Go to the account page for free products.`);
        return;
      }
    
      try {
        await apiClient.addProductToCart(productName, quantity);
        uiStore.setPopupMessage(`Added ${productName} to cart`);
        console.log("adf Added to cart:", productName, quantity);
        trackAddToCart(productName, productName, 'Audio Plugin or Sample Pack', quantity); // Using the gtag event here
        this.fetchCart(); // Re-fetch the entire cart after adding a product
      } catch (error) {
        console.error("Error adding to cart:", error);
      }
    },   
    async removeFromCart(productName: string, quantity: number = 1) {
      const uiStore = useUIStore();
      try {
        await apiClient.removeProductFromCart(productName, quantity);
        uiStore.setPopupMessage(`Removed ${productName} from cart`);
        trackRemoveFromCart(productName); // Using the gtag event here
        this.fetchCart(); // Re-fetch the entire cart after removing a product
      } catch (error) {
        console.error("Error removing from cart:", error);
      }
    },
    async emptyCart() {
      const uiStore = useUIStore();
      try {
        const numItems = this.cartData?.products.length;
        await apiClient.clearCart();
        const itemWord = numItems === 1 ? 'item' : 'items';
        uiStore.setPopupMessage(`Cart emptied. ${numItems} ${itemWord} removed.`);
        this.fetchCart(); // Re-fetch the entire cart after removing a product
      } catch (error) {
        console.error("Error removing from cart:", error);
      }
    },
    async checkOut() {
      console.log("checkOut");  
      // Construct the desired URLs based on the current app's address
      const appOrigin = window.location.origin;
      const backlinkUrl = `${appOrigin}/shop`;
      const landingPageUrl = `${appOrigin}/account?source=purchase`;
      const uiStore = useUIStore();
      uiStore.openLoadingScreen();
    
      apiClient.checkoutCart(backlinkUrl, landingPageUrl)
        .then(response => {
          console.log(response, "checkoutCart");
          if (response && response.valid) {
            console.log("Redirecting to checkout page...", response);
            trackBeginCheckout();
    
            // Attempt to open in a new tab
            const newTab = window.open(response.result.cart_url, '_blank');
    
            // Check if the new tab was blocked
            if (!newTab || newTab.closed || typeof newTab.closed == 'undefined') {
              console.log("Popup blocked. Redirecting in the same tab...");
              window.location.href = response.result.cart_url; // Redirect in the same tab
            } else {
              newTab.focus();
            }
          }
        })
        .catch(error => {
          console.error("Error checking out cart:", error);
        });
    },
    sendWarningNotification(message: string) {
      // Implementierung der Warnungsbenachrichtigung
      // Dies könnte eine API-Anfrage an Ihr Logging-System sein
      console.log('Sending warning notification:', message);
      // Beispiel: 
      // await fetch('/api/log-warning', { 
      //   method: 'POST', 
      //   body: JSON.stringify({ message }) 
      // });
    },
    async buyNow(products: BuyNowProduct[])
    {
      console.log("buyNow", ' products: ', products);
      // Construct the desired URLs based on the current app's address
      const uiStore = useUIStore();
      uiStore.openLoadingScreen();
      const appOrigin = window.location.origin;
      const backlinkUrl = `${appOrigin}/shop`;
      const landingPageUrl = `${appOrigin}/account?source=purchase`;
      console.log("buyNow", ' backlinkUrl: ', backlinkUrl, ' landingPageUrl: ', landingPageUrl);
    
      apiClient.buyNow(products, backlinkUrl, landingPageUrl)
        .then(response => {
          console.log(response, "buyNow");
          if (response && response.valid) {
            console.log("Redirecting to checkout page...", response);
            products.forEach(product => {
              trackAddToCart(product.name, product.name, 'Audio Plugin', products.length); // gtag event for each product
            });
            if(response.result.cart_url)
            {
              trackBeginCheckout();           
              window.open(response.result.cart_url, '_blank').focus();
            } 
            else
              console.error("Error buying now: cart_url is empty", response);
          }
        })
        .catch(error => {
          console.error("Error buying now:", error);
        });
    },
    isInCart(productName) {
      return this.cartData?.products.some(product => product.name === productName) || false;
    },
  },
  
  getters: {
    getCartData() {
      return this.cartData;
    },
    getQuantity(): number {
      return this.quantity;
    }
  }
});

export { useCartStore };
